/*
 * Copyright (c) 2024, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useEffect, useLayoutEffect, useState } from "react";
import {
  ApiReducer,
  ApplicationState,
  Subscription,
  SubscriptionList,
  SubscriptionPoliciesReducer,
  SubscriptionsReducer,
  ToSubscribeAttributes,
  UserReducer,
} from "../../../data/interfaces";
import { connect } from "react-redux";
import { useStyles } from "./styles";
import AppInfoBar from "../../shared/AppInfoBar";
import { FormattedMessage } from "react-intl";
import { ArrowBack, Info } from "@material-ui/icons";
import {
  API_PAGINATION_QUERY,
  MAJOR_VERSION,
  PATH_SEPARATOR,
  VALUE_ZERO,
} from "../../../data/constants";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import { formatSubscriptionApiVersion, getApiMajorVersionRange } from ".";
import {
  createSubscription,
  fetchApiListAuthenticated,
  fetchSubscriptionPoliciesList,
  fetchSubscriptions,
  updateSubscription,
} from "../../../data/operations";
import Settings from "../../../conf/settings";
import { PreLoaderLogo } from "../../../elements/PreLoaderLogo";
import SubscriptionPolicyCard from "./SubscriptionPolicyCard";
import Autocomplete from "@material-ui/lab/Autocomplete";

interface ApplicationApiSubscriptionsProps {
  menuDrawerOpen: boolean;
  organizationHandle: string;
  applicationId: string;
  subscriptionId: string;
  apiReducer: ApiReducer;
  dispatchFetchApis: (
    organizationId: string,
    pagination?: string,
    requireThumbnails?: boolean,
    aggregateBy?: string
  ) => void;
  userReducer: UserReducer;
  fetchSubscriptionPoliciesList: (organizationId: string) => Promise<void>;
  subscriptionPoliciesReducer: SubscriptionPoliciesReducer;
  subscriptionReducer: SubscriptionsReducer;
  dispatchFetchSubscriptionsList: (
    applicationId: string,
    organizationId: string,
    type: string,
    limit?: number,
    offset?: number
  ) => void;
  dispatchCreateSubscription: (
    appId: string,
    organizationId: string,
    apiId: string,
    throttlingPolicy: string,
    versionRange?: string,
    message?: string
  ) => void;
  updateSubscription: (
    subscriptionId: string,
    subscription: Subscription,
    organizationId: string,
    message?: string
  ) => Promise<void>;
}

function ApplicationApiSubscriptions(props: ApplicationApiSubscriptionsProps) {
  const {
    organizationHandle,
    applicationId,
    apiReducer,
    dispatchFetchApis,
    userReducer,
    fetchSubscriptionPoliciesList,
    subscriptionPoliciesReducer,
    subscriptionReducer,
    dispatchFetchSubscriptionsList,
    dispatchCreateSubscription,
    updateSubscription,
    subscriptionId,
  } = props;
  const classes = useStyles();
  const history = useHistory();
  const { selectedOrg } = userReducer;
  const [updatedApiList, setUpdatedApiList] = useState<ToSubscribeAttributes[]>(
    []
  );
  const [buttonClicked, setButtonClicked] = useState(false);
  const [availableApiVersions, setAvailableApiVersions] = useState<string[]>(
    []
  );
  const [selectedApi, setSelecetedApi] = useState<ToSubscribeAttributes>();
  const [selectedApiVersion, setSelecetedApiVersion] = useState<string>();
  const [selectedSubPolicy, setSelectedSubPolicy] = useState<string>();
  const [subscribedApiList, setSubscribedApiList] = useState<
    { name: string; versionRange: string; selectedSubPolicy: string }[]
  >([]);
  const { apiList, loading: apiLoading } = apiReducer;
  const { subscriptionPolicies, loading: isSubpoliciesLoading } =
    subscriptionPoliciesReducer;
  const { subscriptions, isLoading: isSubscriptionsLoading } =
    subscriptionReducer;
  const apis = apiList;
  const DEFAULT_API_VERSION = "Select Version";

  const handleBackButtonClick = () => {
    history.push(
      PATH_SEPARATOR +
        organizationHandle +
        PATH_SEPARATOR +
        "applications" +
        PATH_SEPARATOR +
        applicationId +
        PATH_SEPARATOR +
        "subscriptions"
    );
  };

  const defaultApi: ToSubscribeAttributes = {
    api: "Select API",
    apiId: "",
    version: DEFAULT_API_VERSION,
    availablePolicies: [],
    hasSubscribed: false,
    policy: "",
  };

  const MAXIMUM_API_COUNT = 1000;
  const paginationQuery = API_PAGINATION_QUERY.replace(
    "{LIMIT}",
    MAXIMUM_API_COUNT.toString()
  ).replace("{OFFSET}", VALUE_ZERO.toString());

  const updateApiData = () => {
    const data: ToSubscribeAttributes[] = [];
    let subscribedApiItem;
    if (apis?.count > 0) {
      apis?.list?.forEach((api) => {
        let subscribed = false;
        subscribedApiItem = subscribedApiList?.find(
          (subApi) =>
            subApi.name === api.name &&
            (subApi.versionRange === getApiMajorVersionRange(api.version) ||
              subApi.versionRange === api.version)
        );
        if (subscribedApiItem) {
          subscribed = true;
        }
        data.push({
          api: api.displayName,
          apiId: api.id,
          version: formatSubscriptionApiVersion(api.version),
          availablePolicies: api.throttlingPolicies,
          hasSubscribed: subscribed,
          policy: subscribedApiItem?.selectedSubPolicy,
        });
      });
    }
    return data;
  };

  const updateSubscribedApiList = () => {
    const mappedSubscribedApiList = subscriptions?.list?.map((subscription) => {
      const apiVersion = subscription?.apiInfo?.version;
      const subPolicy = subscription?.throttlingPolicy;
      if (apiVersion) {
        const versionRange =
          Settings.intelligentRoutingFeatureEnabled &&
          apiVersion.startsWith("v")
            ? apiVersion.split(".")[0]
            : apiVersion;
        return {
          name: subscription.apiInfo?.name || "",
          versionRange,
          selectedSubPolicy: subPolicy,
        };
      }
      return {
        name: subscription.apiInfo?.name || "",
        versionRange: "",
        selectedSubPolicy: subPolicy,
      };
    });
    setSubscribedApiList(mappedSubscribedApiList);
  };

  const getAvailableVersions = (
    apiName: string,
    apiList: ToSubscribeAttributes[]
  ) => {
    if (subscriptionId !== undefined) {
      return apiList
        .filter((api) => api.api === apiName)
        .map((api) => api.version)
        .filter((version) => version !== undefined) as string[];
    }
    return apiList
      .filter((api) => api.api === apiName && !api.hasSubscribed)
      .map((api) => api.version)
      .filter((version) => version !== undefined) as string[];
  };

  const removeSubscribedVersions = (versions: string[]) => {
    const apisList = subscribedApiList
      .filter(
        (api) => api.name.toLowerCase() === selectedApi?.api?.toLowerCase()
      )
      .map((api) => api.versionRange);
    return versions.filter(
      (version) => !apisList.includes(version.split(".")[0])
    );
  };

  const changeSubPolicy = (throttlingPolicy: string) => {
    setSelecetedApi({
      ...selectedApi,
      policy: throttlingPolicy,
    });
    setSelectedSubPolicy(throttlingPolicy);
  };

  const getSubscriptionByApplication = (
    subscriptions: SubscriptionList,
    selectedApiId: string,
    selectedApiVersion: string
  ): Subscription => {
    return (
      subscriptions.list.find((subscription) => {
        if (subscription.apiInfo) {
          const { id } = subscription.apiInfo;
          return (
            id === selectedApiId &&
            selectedApiVersion === subscription.versionRange
          );
        }
        return false;
      }) || ({} as Subscription)
    );
  };

  const createSubscription = async () => {
    const apiForVersion = updatedApiList.find(
      (api) =>
        api.version === selectedApiVersion && api.api === selectedApi?.api
    );
    if (selectedApi?.hasSubscribed) {
      const subscriptionData = getSubscriptionByApplication(
        subscriptions,
        selectedApi?.apiId as string,
        getApiMajorVersionRange(selectedApiVersion as string) as string
      );
      const payload: Subscription = {
        applicationId: subscriptionData.applicationId,
        apiId: subscriptionData.apiId,
        throttlingPolicy:
          subscriptionData.throttlingPolicy ?? (selectedSubPolicy as string),
        requestedThrottlingPolicy: selectedSubPolicy as string,
        status: subscriptionData.status,
        subscriptionId: subscriptionData.subscriptionId,
      };
      await updateSubscription(
        subscriptionData.subscriptionId,
        payload,
        selectedOrg.uuid,
        'Subscription update requested successfully'
      );
    } else {
      await dispatchCreateSubscription(
        applicationId as string,
        selectedOrg.uuid,
        apiForVersion?.apiId as string,
        selectedSubPolicy as string,
        Settings.intelligentRoutingFeatureEnabled &&
          selectedApiVersion?.startsWith('v')
          ? selectedApiVersion.split('.')[0]
          : undefined,
        subscriptionPolicies.list.find(
          (policy) => policy.name === selectedSubPolicy
        )?.attributes?.isApprovalRequired
          ? 'Subscription requested successfully'
          : 'Subscription created successfully'
      );
      setSelecetedApi({
        ...selectedApi,
        hasSubscribed: true,
        policy: selectedSubPolicy,
      });
    }
    setButtonClicked(true);
    updateSubscribedApiList();
    handleBackButtonClick();
  };

  const handleApiSelection = (value: string) => {
    setUpdatedApiList(updateApiData());
    const matchedApisForUserSelection = updatedApiList.filter(
      (api) => api.api === value && !api.hasSubscribed
    );
    const isSingleMatch = matchedApisForUserSelection.length === 1;
    const selectedApi = isSingleMatch
      ? matchedApisForUserSelection[0]
      : getUniqueAPIList().find((api) => api?.api === value);
    const versionList = getAvailableVersions(
      value,
      isSingleMatch ? matchedApisForUserSelection : updatedApiList
    );

    setSelecetedApi(selectedApi);
    setAvailableApiVersions(versionList);
    setSelecetedApiVersion(versionList[0]);
    setSelectedSubPolicy(selectedApi?.policy);
  };

  const handleApiVersionSelection = (value: string) => {
    if (value === null) {
      return;
    }
    const userSelectedApi = updatedApiList.find(
      (api) => api.api === selectedApi?.api && api.version === value
    );
    setSelecetedApi(userSelectedApi);
    setSelecetedApiVersion(value as string);
    setSelectedSubPolicy(userSelectedApi?.policy);
    const versionList = getAvailableVersions(
      selectedApi?.api as string,
      updatedApiList
    );
    setAvailableApiVersions(removeSubscribedVersions(versionList));
  };

  useLayoutEffect(() => {
    if (applicationId) {
      dispatchFetchSubscriptionsList(
        applicationId,
        selectedOrg.uuid,
        "application",
        150,
        0
      );
    }
    if (Settings.intelligentRoutingFeatureEnabled) {
      dispatchFetchApis(
        selectedOrg.uuid,
        paginationQuery,
        false,
        MAJOR_VERSION
      );
    } else {
      dispatchFetchApis(selectedOrg.uuid, paginationQuery, false);
    }
    fetchSubscriptionPoliciesList(selectedOrg.uuid);
  }, []);

  useEffect(() => {
    if (subscriptionId && updateApiData().length > 0) {
      const newAPIData = updateApiData();
      const matchedSubscription = subscriptions.list.find(
        (subscription) => subscription.subscriptionId === subscriptionId
      );
      if (matchedSubscription) {
        const subscribedApi = newAPIData.find(
          (api) => api.apiId === matchedSubscription.apiInfo?.id
        );
        // The API list is aggregated by major version, so the subscribed version is missing from it
        // once a newer minor version is published. Fall back to the subscription's own API info.
        const subscribedApiVersion =
          subscribedApi?.version ??
          (matchedSubscription.apiInfo?.version
            ? formatSubscriptionApiVersion(matchedSubscription.apiInfo.version)
            : undefined);
        setSelecetedApi({
          policy: matchedSubscription.throttlingPolicy,
          api: matchedSubscription.apiInfo?.displayName,
          version: subscribedApiVersion,
          apiId: matchedSubscription.apiInfo?.id,
          availablePolicies:
            subscribedApi?.availablePolicies ??
            matchedSubscription.apiInfo?.throttlingPolicies,
          hasSubscribed: true,
        });
        setSelecetedApiVersion(subscribedApiVersion);
        setSelectedSubPolicy(matchedSubscription.throttlingPolicy);
        const gversions = getAvailableVersions(
          matchedSubscription.apiInfo?.displayName as string,
          updatedApiList
        );
        setAvailableApiVersions(gversions);
      }
    } else if (
      subscriptionId === undefined &&
      selectedApi === undefined &&
      updatedApiList.length > 0 &&
      updatedApiList[0].api
    ) {
      const firstUniqueApi = getUniqueAPIList()[0];
      setSelecetedApi(firstUniqueApi);
      if (firstUniqueApi) {
        setSelecetedApi({
          policy: undefined,
          api: firstUniqueApi.api,
          version: firstUniqueApi.version,
          apiId: firstUniqueApi.apiId,
          availablePolicies: firstUniqueApi.availablePolicies,
          hasSubscribed: false,
        });
      }
      setSelecetedApi(defaultApi);
      setSelecetedApiVersion(DEFAULT_API_VERSION);
      setSelectedSubPolicy("");
    }
  }, [updatedApiList]);

  useEffect(() => {
    updateSubscribedApiList();
    setButtonClicked(false);
  }, [subscriptions, buttonClicked]);

  useEffect(() => {
    if (apis?.list && apis?.list.length > 0) {
      setUpdatedApiList(updateApiData());
    }
  }, [apis?.list]);

  const getUniqueAPIList = () => {
    const listToFilter =
      subscriptionId === undefined
        ? updatedApiList.filter((api) => !api.hasSubscribed)
        : updatedApiList;

    const uniqueApis = Array.from(
      new Set(listToFilter.map((api) => api.api))
    ).map((name) => listToFilter.find((api) => api.api === name));

    return uniqueApis;
  };

  const APISelector = () => {
    return (
      <div>
        <FormControl
          variant="outlined"
          data-testid="application.subscriptions.api.select.formcontrol"
        >
          <Autocomplete
            className={classes.autoComplete}
            options={getUniqueAPIList().map((api) => api?.api || "")}
            getOptionLabel={(option) => option || ""}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" />
            )}
            disabled={subscriptionId !== undefined}
            onChange={(_, value) => handleApiSelection(value as string)}
            value={selectedApi?.api || ""}
          />
        </FormControl>
      </div>
    );
  };

  const ApiVersionSelector = () => {
    return (
      <div>
        <FormControl
          variant="outlined"
          data-testid="application-subscriptoin-api-version-select"
        >
          <Autocomplete
            className={classes.autoComplete}
            options={availableApiVersions}
            getOptionLabel={(option) => option || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                className="classes.autoCompleteTextField"
              />
            )}
            disabled={subscriptionId !== undefined}
            onChange={(_, value) => handleApiVersionSelection(value as string)}
            value={selectedApi?.version || ""}
          />
        </FormControl>
      </div>
    );
  };

  const isSubPolicySlected = (policy: string) => {
    const matchedApi = subscribedApiList.find(
      (api) =>
        api.name.toLowerCase() === selectedApi?.api?.toLowerCase() &&
        api.versionRange === selectedApiVersion?.split(".")[0] &&
        api.selectedSubPolicy === policy
    );
    if (matchedApi === undefined) {
      return false;
    }
    return true;
  };

  const SubscriptionPolicySelector = () => {
    return (
      <Grid
        item={true}
        xs={12}
        md={12}
        lg={12}
        className={classes.policySelectorGridItem}
      >
        {selectedApi?.availablePolicies?.length == 0 && (
          <Grid item xs={12} md={12} lg={12}>
            <div className={classes.noDataInfo}>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
              >
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      variant="subtitle2"
                      className={classes.noDataText}
                    >
                      <FormattedMessage
                        id="application.subscriptions.api.noPolicies"
                        defaultMessage="Select an API to view available subscription plans."
                      />
                    </Typography>
                  </Box>
                  <Box display="flex">
                    <Info className={classes.infoIcon} />
                  </Box>
                </Box>
              </Box>
            </div>
          </Grid>
        )}
        {selectedApi?.availablePolicies?.map((policyName) => {
          return (
            <Grid item xs={3} md={3} lg={3} key={policyName}>
              {subscriptionPolicies.list.map((subPolicy) => {
                if (subPolicy.name === policyName) {
                  return (
                    <SubscriptionPolicyCard
                      subscriptionPolicy={subPolicy}
                      isSelected={
                        selectedApi.policy === undefined
                          ? isSubPolicySlected(subPolicy.name)
                          : subPolicy.name === selectedApi.policy
                      }
                      handleSubPolicySelection={changeSubPolicy}
                      key={subPolicy.name}
                    />
                  );
                }
              })}
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const subscriptionButtonText = () => {
    if (
      subscriptionPolicies.list.find(
        (policy) => policy.name === selectedSubPolicy
      )?.attributes?.isApprovalRequired
    ) {
      return subscriptionId ? (
        <FormattedMessage
          id="application.subscriptions.api.requestUpdateSubscription.button.label"
          defaultMessage="Request Subscription Update"
        />
      ) : (
        <FormattedMessage
          id="application.subscriptions.api.requestSubscription.button.label"
          defaultMessage="Request Subscription"
        />
      );
    } else {
      return subscriptionId ? (
        <FormattedMessage
          id="application.subscriptions.api.updateSubscription.button.label"
          defaultMessage="Update Subscription"
        />
      ) : (
        <FormattedMessage
          id="application.subscriptions.api.addSubscription.button.label"
          defaultMessage="Add Subscription"
        />
      );
    }
  };

  return (
    <main className={classes.content}>
      {apiLoading && isSubpoliciesLoading && isSubscriptionsLoading ? (
        <div className={classes.loaderContainer}>
          <PreLoaderLogo size="medium" />
        </div>
      ) : (
        <div>
          <main className={classes.content}>
            <div className={classes.appBarSpacer} />
            <AppInfoBar />
            <Container
              maxWidth="lg"
              className={classes.container}
              classes={{ root: classes.componentPadding }}
            >
              <Grid container={true} spacing={2}>
                <Grid item={true} xs={12} md={12} lg={12} direction={"row"}>
                  <div className={classes.titleWrapper}>
                    <Button
                      color="primary"
                      onClick={() => handleBackButtonClick()}
                      data-testid="generate-creds-btn"
                      className={classes.backToSubscriptionBtn}
                      startIcon={<ArrowBack />}
                    >
                      <FormattedMessage
                        id="application.newsubscriptions.buttons.back"
                        defaultMessage="Back to Subscription List"
                      />
                    </Button>
                  </div>
                  <Grid item={true} xs={12} md={12} lg={12} direction={"row"}>
                    {subscriptionId ? (
                      <Typography className={classes.subscriptionsTitle}>
                        <FormattedMessage
                          id="application.subscriptions.update.title"
                          defaultMessage="Update Subscription"
                        />
                      </Typography>
                    ) : (
                      <Typography className={classes.subscriptionsTitle}>
                        <FormattedMessage
                          id="application.subscriptions.add.title"
                          defaultMessage="Add APIs"
                        />
                      </Typography>
                    )}
                  </Grid>
                </Grid>
                <Grid
                  item={true}
                  xs={12}
                  md={12}
                  lg={12}
                  className={classes.gridItem}
                >
                  <Box className={classes.selectorBox}>
                    <Box className={classes.selector}>
                      <FormattedMessage
                        id="application.subscriptions.api.name"
                        defaultMessage="API Name"
                      />
                      <APISelector />
                    </Box>
                    <Box>
                      <FormattedMessage
                        id="application.subscriptions.api.version"
                        defaultMessage="API Version"
                      />
                      <ApiVersionSelector />
                    </Box>
                  </Box>
                </Grid>
                <Grid
                  item={true}
                  xs={12}
                  md={12}
                  lg={12}
                  className={classes.gridItem}
                >
                  <FormattedMessage
                    id="application.subscriptions.api.subscriptionPolicies"
                    defaultMessage="Subscription Plans"
                  />
                </Grid>
                <SubscriptionPolicySelector />
                <Grid item xs={12} md={12} lg={12} className={classes.gridItem}>
                  <Box className={classes.btnBox}>
                    <Button
                      variant="contained"
                      color="default"
                      disableElevation
                      onClick={() => handleBackButtonClick()}
                      className={classes.btnText}
                    >
                      <FormattedMessage
                        id="application.api.subscriptions.cancel.button.label"
                        defaultMessage="Cancel"
                      />
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      disableElevation
                      onClick={createSubscription}
                      className={classes.btnText}
                      disabled={!selectedSubPolicy || isSubscriptionsLoading}
                    >
                      {subscriptionButtonText()}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </main>
        </div>
      )}
    </main>
  );
}

const mapStateToProps = (state: ApplicationState) => {
  return {
    apiReducer: state.apiReducer,
    userReducer: state.userReducer,
    subscriptionPoliciesReducer: state.subscriptionPoliciesReducer,
    subscriptionReducer: state.subscriptionsReducer,
  };
};

const mapDispatchToProps = {
  dispatchFetchApis: fetchApiListAuthenticated,
  fetchSubscriptionPoliciesList: fetchSubscriptionPoliciesList,
  dispatchFetchSubscriptionsList: fetchSubscriptions,
  dispatchCreateSubscription: createSubscription,
  updateSubscription: updateSubscription,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ApplicationApiSubscriptions);
